import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faStar,
  faXmark,
  faShieldHalved,
  faTriangleExclamation,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { getSessionStorage } from 'nsdi-volto-frontend/utils/browserStorage';

interface VerificationWidgetProps {
  onValidated: () => void;
  onClose: () => void;
  verificationBaseUrl?: string;
  turnstileSiteKey?: string;
  allowLocalDevBypass?: boolean;
  headerTitle?: string;
  headerSubtitle?: string;
  showDebugInfo?: boolean;
}

const MAX_RETRIES = 3;

const VerificationWidget: React.FC<VerificationWidgetProps> = ({
  onValidated,
  onClose,
  verificationBaseUrl,
  turnstileSiteKey,
  allowLocalDevBypass,
  headerTitle = 'Enhanced Search',
  headerSubtitle = 'Natural language to advanced search queries',
  showDebugInfo,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadIssue, setLoadIssue] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [failureCount, setFailureCount] = useState(0);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  const isBlocked = failureCount >= MAX_RETRIES;
  const baseUrl = verificationBaseUrl?.replace(/\/$/, '') ?? '';
  const verifyUrl = `${baseUrl}/verify-token`;
  const currentHost =
    typeof window !== 'undefined' ? window.location.hostname : 'n/a';
  const currentOrigin =
    typeof window !== 'undefined' ? window.location.origin : 'n/a';
  const isLocalDevHost =
    currentHost === 'localhost' ||
    currentHost === '127.0.0.1' ||
    currentHost.endsWith('.local');
  const canUseDevBypass = Boolean(allowLocalDevBypass && isLocalDevHost);
  const resolvedTurnstileSiteKey = (
    turnstileSiteKey ||
    process.env.REACT_APP_CF_TURNSTILE_SITE_KEY ||
    process.env.CF_TURNSTILE_SITE_KEY ||
    (window as unknown as { env?: Record<string, string> }).env
      ?.REACT_APP_CF_TURNSTILE_SITE_KEY ||
    ''
  ).trim();
  const maskedSiteKey = resolvedTurnstileSiteKey
    ? `${resolvedTurnstileSiteKey.slice(0, 6)}...${resolvedTurnstileSiteKey.slice(-4)}`
    : 'missing';

  useEffect(() => {
    if (!resolvedTurnstileSiteKey) {
      setLoadIssue(null);
      return;
    }

    setLoadIssue(null);
    const timeoutId = window.setTimeout(() => {
      setLoadIssue(
        'CAPTCHA failed to load. Check that this site domain is allowed for the configured Turnstile site key.',
      );
    }, 8000);

    return () => window.clearTimeout(timeoutId);
  }, [resolvedTurnstileSiteKey]);

  const fail = (message: string) => {
    setIsVerifying(false);
    setErrorMessage(message);
    setFailureCount((prev) => {
      const next = prev + 1;
      if (next < MAX_RETRIES) {
        turnstileRef.current?.reset();
      }
      return next;
    });
  };

  const handleSuccess = async (cfToken: string) => {
    setErrorMessage(null);
    setIsVerifying(true);

    try {
      const res = await fetch(verifyUrl, {
        method: 'POST',
        body: JSON.stringify({ token: cfToken }),
        headers: { 'content-type': 'application/json' },
      });

      if (!res.ok) {
        fail('Verification failed. Please try again.');
        return;
      }

      const data = await res.json();
      if (!data?.success) {
        fail('Verification failed. Please try again.');
        return;
      }

      const sessionToken: string | undefined = data?.session?.token;
      if (!sessionToken) {
        fail('Verification failed. No session token received.');
        return;
      }

      getSessionStorage()?.setItem('sessionToken', sessionToken);
      setIsVerifying(false);
      onValidated();
    } catch {
      fail('Network error. Please check your connection and try again.');
    }
  };

  const handleLocalDevBypass = () => {
    getSessionStorage()?.setItem('sessionToken', 'local-dev-bypass');
    setErrorMessage(null);
    setLoadIssue(null);
    onValidated();
  };

  return (
    <div className="ves-verification-panel">
      <header className="ves-chat-header">
        <div className="ves-chat-header-icon">
          <FontAwesomeIcon icon={faSearch} />
        </div>
        <div className="ves-chat-header-info">
          <h1 className="ves-chat-header-title">
            {headerTitle}
            <FontAwesomeIcon icon={faStar} className="ves-sparkle-icon" />
          </h1>
          <p className="ves-chat-header-subtitle">{headerSubtitle}</p>
        </div>
        <button
          onClick={onClose}
          className="ves-header-button ves-header-button--icon"
          aria-label="Close"
          title="Close"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </header>

      <div className="ves-verification-body">
        {isBlocked ? (
          <>
            <div className="ves-verification-icon ves-verification-icon--error">
              <FontAwesomeIcon icon={faTriangleExclamation} />
            </div>
            <div className="ves-verification-copy">
              <h2>Verification failed</h2>
              <p>
                Too many failed attempts. Please refresh the page and try again,
                or contact support if the problem persists.
              </p>
            </div>
            <button onClick={onClose} className="ves-verification-close-btn">
              Close
            </button>
          </>
        ) : (
          <>
            <div className="ves-verification-icon">
              <FontAwesomeIcon icon={faShieldHalved} />
            </div>
            <div className="ves-verification-copy">
              <h2>Verify you are human</h2>
              <p>
                Complete the quick security check below to start chatting with
                the Enhanced Search Agent.
              </p>
              {failureCount > 0 && (
                <p className="ves-verification-attempt">
                  Attempt {failureCount + 1} of {MAX_RETRIES}
                </p>
              )}
            </div>

            <div className="ves-verification-captcha-wrap">
              {resolvedTurnstileSiteKey ? (
                <Turnstile
                  ref={turnstileRef}
                  siteKey={resolvedTurnstileSiteKey}
                  options={{ theme: 'dark', appearance: 'always' }}
                  onLoad={() => setLoadIssue(null)}
                  onSuccess={handleSuccess}
                  onError={(code) =>
                    setErrorMessage(
                      `CAPTCHA encountered an error${code ? ` (${String(code)})` : ''}. Check your site key and allowed domain.`,
                    )
                  }
                  onExpire={() =>
                    fail(
                      'Verification expired. Please complete the CAPTCHA again.',
                    )
                  }
                  onTimeout={() =>
                    fail('Verification timed out. Please try again.')
                  }
                />
              ) : (
                <p role="alert" className="ves-verification-error">
                  CAPTCHA is not configured. Set Turnstile Site Key in block
                  settings.
                </p>
              )}

              {loadIssue && (
                <p role="alert" className="ves-verification-error">
                  {loadIssue}
                </p>
              )}

              {isVerifying && (
                <div className="ves-verification-overlay">
                  <div className="ves-verification-overlay-text">
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Verifying...
                  </div>
                </div>
              )}
            </div>

            {errorMessage && (
              <p role="alert" className="ves-verification-error">
                {errorMessage}
              </p>
            )}

            {canUseDevBypass && (
              <div className="ves-verification-dev-bypass">
                <p className="ves-verification-dev-bypass-note">
                  Localhost bypass is enabled by block settings.
                </p>
                <button
                  type="button"
                  className="ves-verification-dev-bypass-btn"
                  onClick={handleLocalDevBypass}
                >
                  Continue without CAPTCHA (local dev)
                </button>
              </div>
            )}

            {showDebugInfo && isLocalDevHost && (
              <div
                className="ves-verification-debug"
                role="note"
                aria-live="polite"
              >
                <p>
                  Debug host: <strong>{currentHost}</strong>
                </p>
                <p>
                  Debug origin: <strong>{currentOrigin}</strong>
                </p>
                <p>
                  Debug verify endpoint: <strong>{verifyUrl}</strong>
                </p>
                <p>
                  Debug site key: <strong>{maskedSiteKey}</strong>
                </p>
              </div>
            )}
          </>
        )}
      </div>

      <footer className="ves-verification-footer">
        <p>Protected by Cloudflare Turnstile</p>
      </footer>
    </div>
  );
};

export default VerificationWidget;
